import { ConfigService } from '@config/config.service';
import { ContactsActions, ContactsActionTypes } from '@crm/ngrx/actions/contacts.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ContactResponse } from '@services/associations/model/contactResponse';
import { ContactSearchRequest } from '@services/associations/model/contactSearchRequest';
import { PaginationModel } from '@shared/models/pagination.model';

export const adapter: EntityAdapter<ContactResponse> = createEntityAdapter<ContactResponse>();

export interface State extends EntityState<ContactResponse> {
  loading: boolean,
  pagination: PaginationModel,
  filter: ContactSearchRequest,
}

export const initialState: State = adapter.getInitialState({
  loading: false,
  pagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
  filter: {},
});

export function reducer(state = initialState, action: ContactsActions): State {
  switch (action.type) {
    /** Load Contacts */
    case ContactsActionTypes.LoadContacts:
      return {...state, loading: !action.payload?.dontShowLoading};

    case ContactsActionTypes.LoadContactsSuccess:
      return adapter.setAll(action.payload.content!, {
        ...state,
        loading: false,
        pagination: {
          size: action.payload.size,
          last: action.payload.last,
          totalElements: action.payload.totalElements,
          numberOfElements: action.payload.numberOfElements,
          first: action.payload.first,
          number: action.payload.number,
        },
      });

    case ContactsActionTypes.LoadContactsFailure:
      return {...state, loading: false};

    /** Edit Contact */
    case ContactsActionTypes.EditContact:
      return {...state};

    case ContactsActionTypes.EditContactSuccess:
      return adapter.updateOne({
        id: action.payload.id!,
        changes: action.payload,
      }, {...state});

    case ContactsActionTypes.EditContactFailure:
      return {...state};

    /** Change Page */
    case ContactsActionTypes.ChangePage:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          number: action.payload.number,
          size: action.payload.size,
        }
      };

    /** Set Initial Pagination */
    case ContactsActionTypes.SetInitialPagination:
      return {...state, pagination: initialState.pagination};

    /** Set Filter */
    case ContactsActionTypes.SetFilter:
      return {...state, filter: action.payload};

    /** Clear Filter */
    case ContactsActionTypes.ClearFilter:
      return {...state, filter: initialState.filter};

    /** Set Initial State */
    case ContactsActionTypes.SetInitialState:
      return {...initialState};

    default:
      return state;
  }
}

export const {
  selectIds: selectContactsIds,
  selectEntities: selectContactsEntities,
  selectAll: selectAllContacts,
  selectTotal: selectContactsTotal,
} = adapter.getSelectors();
